import { useState, useEffect, useCallback } from "react";

import WebApp from "@twa-dev/sdk";

import { PopupParams } from "@twa-dev/types";
import { SafeAreaInset } from "@twa-dev/types";

import { initData as TG_INIT_DATA } from "../miniapp.config";

export interface User {
  id: number;
  firstName: string;
  lastName?: string;
  username?: string;
  languageCode: string;
  isPremium: boolean;
  photoUrl?: string;
}

const initialUserState: User = {
  id: 0,
  firstName: "",
  lastName: "",
  username: "",
  languageCode: "en",
  isPremium: false,
  photoUrl: "",
};

export const useTelegram = () => {
  const [user, setUser] = useState<User>(initialUserState);
  const [initData, setInitData] = useState<string>("");
  const [platform, setPlatform] = useState<string>("");
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [contentSafeAreaInset, setContentSafeAreaInset] =
    useState<SafeAreaInset>({
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    });

  useEffect(() => {
    if (WebApp.platform !== "unknown") {
      setUser({
        id: WebApp.initDataUnsafe?.user?.id ?? 0,
        firstName: WebApp.initDataUnsafe?.user?.first_name ?? "",
        lastName: WebApp.initDataUnsafe?.user?.last_name ?? "",
        username: WebApp.initDataUnsafe?.user?.username ?? "",
        languageCode: WebApp.initDataUnsafe?.user?.language_code ?? "",
        isPremium: WebApp.initDataUnsafe?.user?.is_premium ?? false,
        photoUrl: WebApp.initDataUnsafe.user?.photo_url ?? "",
      });
      setInitData(WebApp.initData);
    } else {
      setUser({
        id: 0,
        firstName: "Ultron",
        lastName: "Labs",
        username: "ultronlabs",
        languageCode: "en",
        isPremium: false,
      });
      setInitData(TG_INIT_DATA);
    }
    setPlatform(WebApp.platform);
    setIsFullScreen(WebApp.isFullscreen);
    setContentSafeAreaInset(WebApp.contentSafeAreaInset);
  }, []);

  const initMiniApp = useCallback(() => {
    WebApp.expand();
    WebApp.enableClosingConfirmation();
    WebApp.setHeaderColor("#02170e");
    WebApp.setBackgroundColor("#02170e");
    WebApp.setBottomBarColor("#02170e");
    WebApp.disableVerticalSwipes();
    WebApp.SettingsButton.hide();
  }, []);

  return {
    user,
    initData,
    startParams: WebApp.initDataUnsafe?.start_param,
    platform,
    initMiniApp,
    isFullScreen,
    contentSafeAreaInset,
    CloudStorage: WebApp.CloudStorage,
  };
};

export const useTelegramCloudStorage = () => {
  const getItem = useCallback(
    (
      key: string,
      callback: (error: string | null, result?: string) => void
    ) => {
      try {
        WebApp.CloudStorage.getItem(key, callback);
      } catch (error) {
        console.error(error);
        const result = localStorage.getItem(key);
        callback(null, result ?? undefined);
      }
    },
    []
  );

  const setItem = useCallback(
    (key: string, value: string, callback: (error: string | null) => void) => {
      try {
        WebApp.CloudStorage.setItem(key, value, callback);
      } catch (error) {
        console.error(error);
        localStorage.setItem(key, value);
        callback(null);
      }
    },
    []
  );

  return { getItem, setItem };
};

export const useTelegramHaptics = () => {
  const hapticFeedback = useCallback(
    (type: "light" | "medium" | "heavy" | "soft" | "rigid") => {
      WebApp.HapticFeedback.impactOccurred(type);
    },
    []
  );

  const selectionFeedback = useCallback(() => {
    WebApp.HapticFeedback.selectionChanged();
  }, []);

  return { hapticFeedback, selectionFeedback };
};

export const useTelegramShare = () => {
  const shareUrl = useCallback((url: string, text: string) => {
    try {
      WebApp.openTelegramLink(
        "https://t.me/share/url?url=" + url + "&text=" + text
      );
    } catch {
      window.open(url, "_blank");
    }
  }, []);

  const openLink = useCallback((url: string) => {
    WebApp.openLink(url);
  }, []);

  return { shareUrl, openLink };
};

export const useTelegramButton = () => {
  const showBackButton = useCallback((onClick: () => void) => {
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(onClick);
  }, []);

  const hideBackButton = useCallback(() => {
    WebApp.BackButton.hide();
  }, []);

  return { showBackButton, hideBackButton };
};

export const useTelegramNotification = () => {
  const showPopup = useCallback((params: PopupParams) => {
    WebApp.showPopup(params);
  }, []);

  const showAlert = useCallback((message: string, onClose: () => void) => {
    WebApp.showAlert(message, onClose);
  }, []);

  const showConfirm = useCallback((message: string, onConfirm: () => void) => {
    WebApp.showConfirm(message, onConfirm);
  }, []);

  return { showPopup, showAlert, showConfirm };
};

export const useColorScheme = () => {
  const [colorScheme, setColorScheme] = useState<"light" | "dark">(
    WebApp.colorScheme
  );

  useEffect(() => {
    const handleColorSchemeChange = () => {
      setColorScheme(WebApp.colorScheme);
    };

    if (WebApp.platform !== "unknown") {
      WebApp.onEvent("themeChanged", handleColorSchemeChange);
      return () => WebApp.offEvent("themeChanged", handleColorSchemeChange);
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setColorScheme(mediaQuery.matches ? "dark" : "light");

    const handleChange = (e: MediaQueryListEvent) => {
      setColorScheme(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleColorScheme = useCallback(() => {
    setColorScheme((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  const toggleTopBarMainScreenColor = useCallback(() => {
    // WebApp.setHeaderColor(colorScheme === "light" ? "#ffffff" : "#1b1b1b");
    // WebApp.setBackgroundColor(colorScheme === "light" ? "#ffffff" : "#1b1b1b");
    // WebApp.setBottomBarColor(colorScheme === "light" ? "#ffffff" : "#1b1b1b");
    WebApp.setHeaderColor("#ffffff");
    WebApp.setBackgroundColor("#ffffff");
    WebApp.setBottomBarColor("#ffffff");
  }, []);

  const toggleTopBarColor = useCallback(() => {
    WebApp.setHeaderColor("#e848b5");
    WebApp.setBackgroundColor("#e848b5");
    WebApp.setBottomBarColor("#e848b5");
  }, []);

  return {
    colorScheme,
    toggleColorScheme,
    toggleTopBarMainScreenColor,
    toggleTopBarColor,
  };
};
